/**
 * Cross-cutting frontend contracts shared across multiple features.
 * Keep this file intentionally small; feature-specific payloads belong in feature-local type files under src/features/.
 */
export type { ApiV1ContractsByEndpoint } from '../types/apiV1';

export enum UserRole {
  CUSTOMER = 'CUSTOMER',
  DRIVER = 'DRIVER',
  IN_STORE_SHOPPER = 'IN_STORE_SHOPPER',
  ADMIN = 'ADMIN',
  OWNER = 'OWNER',
  AFFILIATE = 'AFFILIATE'
}

export enum UserTier {
  COMMON = 'COMMON',
  BRONZE = 'BRONZE',
  SILVER = 'SILVER',
  GOLD = 'GOLD',
  PLATINUM = 'PLATINUM',
  GREEN = 'GREEN'
}

/**
 * Mirrors `server/modules/orders/lib/order-states.constants.ts` `ORDER_INTERNAL_STATES`.
 * The frontend keeps the full enum so callers can transmit canonical states without
 * lossy normalization, while UI surfaces should always render the
 * `customerFacingStatus()` helper output instead of the raw enum.
 */
export enum OrderStatus {
  PENDING_AUTH = 'PENDING_AUTH',
  PENDING_PARENT_APPROVAL = 'PENDING_PARENT_APPROVAL',
  AUTHORIZED = 'AUTHORIZED',
  AUTH_FAILED = 'AUTH_FAILED',
  ASSIGNED = 'ASSIGNED',
  EN_ROUTE_TO_STORE = 'EN_ROUTE_TO_STORE',
  SHOPPING = 'SHOPPING',
  CUSTOMER_REVIEW_REQUIRED = 'CUSTOMER_REVIEW_REQUIRED',
  EXTRA_STORE_PENDING = 'EXTRA_STORE_PENDING',
  EXTRA_STORE_APPROVED = 'EXTRA_STORE_APPROVED',
  SHOPPING_EXTRA_STORE = 'SHOPPING_EXTRA_STORE',
  CHECKOUT_AT_STORE = 'CHECKOUT_AT_STORE',
  CAPTURING = 'CAPTURING',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  ARRIVING = 'ARRIVING',
  DELIVERED = 'DELIVERED',
  CAPTURED = 'CAPTURED',
  REFUNDED = 'REFUNDED',
  DISPUTED = 'DISPUTED',
  NEEDS_PAYMENT_RESOLUTION = 'NEEDS_PAYMENT_RESOLUTION',
  CANCELED = 'CANCELED',
  EXPIRED = 'EXPIRED',
  CLOSED = 'CLOSED',
  /**
   * @deprecated Legacy alias kept only so older payloads/UI labels keep compiling.
   * Server consolidated to `PENDING_AUTH`; new callers should use that.
   */
  PENDING = 'PENDING_AUTH',
  /**
   * @deprecated Legacy alias for `OUT_FOR_DELIVERY`.
   */
  PICKED_UP = 'OUT_FOR_DELIVERY',
  /**
   * @deprecated Not a server-side order state. Customer refund requests now go
   * through `POST /api/v1/refunds`. Retained only for legacy UI labels.
   */
  REFUND_REQUESTED = 'REFUND_REQUESTED'
}

/**
 * High-level customer-facing buckets the storefront renders. Driver/admin UIs
 * may show the raw `OrderStatus` value, but customer-facing surfaces should
 * collapse intermediate fulfillment states down to one of these labels.
 */
export type CustomerFacingOrderStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'SHOPPING'
  | 'AWAITING_DECISION'
  | 'OUT_FOR_DELIVERY'
  | 'ARRIVING'
  | 'DELIVERED'
  | 'REFUNDED'
  | 'DISPUTED'
  | 'CANCELED'
  | 'PAYMENT_ISSUE'
  | 'CLOSED';

const CUSTOMER_FACING_BY_STATUS: Record<string, CustomerFacingOrderStatus> = {
  PENDING_AUTH: 'PENDING',
  PENDING_PARENT_APPROVAL: 'AWAITING_DECISION',
  PENDING: 'PENDING',
  AUTHORIZED: 'CONFIRMED',
  AUTH_FAILED: 'PAYMENT_ISSUE',
  ASSIGNED: 'CONFIRMED',
  EN_ROUTE_TO_STORE: 'SHOPPING',
  SHOPPING: 'SHOPPING',
  CUSTOMER_REVIEW_REQUIRED: 'AWAITING_DECISION',
  EXTRA_STORE_PENDING: 'AWAITING_DECISION',
  EXTRA_STORE_APPROVED: 'SHOPPING',
  SHOPPING_EXTRA_STORE: 'SHOPPING',
  CHECKOUT_AT_STORE: 'SHOPPING',
  CAPTURING: 'OUT_FOR_DELIVERY',
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
  PICKED_UP: 'OUT_FOR_DELIVERY',
  ARRIVING: 'ARRIVING',
  DELIVERED: 'DELIVERED',
  CAPTURED: 'DELIVERED',
  REFUNDED: 'REFUNDED',
  DISPUTED: 'DISPUTED',
  REFUND_REQUESTED: 'REFUNDED',
  NEEDS_PAYMENT_RESOLUTION: 'PAYMENT_ISSUE',
  PAYMENT_ISSUE: 'PAYMENT_ISSUE',
  CANCELED: 'CANCELED',
  CANCELLED: 'CANCELED',
  EXPIRED: 'CANCELED',
  CLOSED: 'CLOSED'
};

/**
 * Map any status string (canonical or legacy alias) to the
 * `CustomerFacingOrderStatus` bucket the storefront renders.
 */
export const customerFacingStatus = (value: unknown): CustomerFacingOrderStatus => {
  const raw = String(value ?? '').trim().toUpperCase();
  return CUSTOMER_FACING_BY_STATUS[raw] ?? 'PENDING';
};

/** Maps API / websocket / legacy strings to canonical `OrderStatus` (mirrors server internal states). */
export const normalizeCanonicalOrderStatus = (value: unknown): OrderStatus => {
  const raw = String(value ?? '').trim().toUpperCase();
  const aliases: Record<string, OrderStatus> = {
    PENDING_AUTH: OrderStatus.PENDING_AUTH,
    PENDING_PARENT_APPROVAL: OrderStatus.PENDING_PARENT_APPROVAL,
    AUTHORIZED: OrderStatus.AUTHORIZED,
    AUTH_FAILED: OrderStatus.AUTH_FAILED,
    ASSIGNED: OrderStatus.ASSIGNED,
    EN_ROUTE_TO_STORE: OrderStatus.EN_ROUTE_TO_STORE,
    SHOPPING: OrderStatus.SHOPPING,
    CUSTOMER_REVIEW_REQUIRED: OrderStatus.CUSTOMER_REVIEW_REQUIRED,
    EXTRA_STORE_PENDING: OrderStatus.EXTRA_STORE_PENDING,
    EXTRA_STORE_APPROVED: OrderStatus.EXTRA_STORE_APPROVED,
    SHOPPING_EXTRA_STORE: OrderStatus.SHOPPING_EXTRA_STORE,
    CHECKOUT_AT_STORE: OrderStatus.CHECKOUT_AT_STORE,
    CAPTURING: OrderStatus.CAPTURING,
    OUT_FOR_DELIVERY: OrderStatus.OUT_FOR_DELIVERY,
    ARRIVING: OrderStatus.ARRIVING,
    DELIVERED: OrderStatus.DELIVERED,
    CAPTURED: OrderStatus.CAPTURED,
    REFUNDED: OrderStatus.REFUNDED,
    DISPUTED: OrderStatus.DISPUTED,
    NEEDS_PAYMENT_RESOLUTION: OrderStatus.NEEDS_PAYMENT_RESOLUTION,
    CANCELED: OrderStatus.CANCELED,
    CANCELLED: OrderStatus.CANCELED,
    EXPIRED: OrderStatus.EXPIRED,
    CLOSED: OrderStatus.CLOSED,
    PENDING: OrderStatus.PENDING_AUTH,
    CART: OrderStatus.PENDING_AUTH,
    ACCEPTED: OrderStatus.ASSIGNED,
    PICKED_UP: OrderStatus.OUT_FOR_DELIVERY,
    EN_ROUTE_TO_EXTRA_STORE: OrderStatus.SHOPPING_EXTRA_STORE,
    PAYMENT_ISSUE: OrderStatus.NEEDS_PAYMENT_RESOLUTION,
    PAID: OrderStatus.AUTHORIZED
  };
  return aliases[raw] ?? OrderStatus.PENDING_AUTH;
};

export enum ScannerMode {
  INVENTORY_CREATE = 'INVENTORY_CREATE',
  UPC_LOOKUP = 'UPC_LOOKUP',
  DRIVER_VERIFY_CONTAINERS = 'DRIVER_VERIFY_CONTAINERS',
  DRIVER_FULFILL_ORDER = 'DRIVER_FULFILL_ORDER',
  CUSTOMER_RETURN_SCAN = 'CUSTOMER_RETURN_SCAN',
  RECEIPT_PARSE_LIVE = 'RECEIPT_PARSE_LIVE'
}

export type PaymentMethod = 'STRIPE_CARD' | 'GOOGLE_PAY' | 'CREDITS';
export type SizeUnit = 'oz' | 'fl oz' | 'g' | 'kg' | 'ml' | 'l';
export type UpcContainerType = 'aluminum' | 'glass' | 'plastic';

export interface AddressObject {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface ShopperPreferences {
  allowSubstitutions: boolean;
  refundMissingItems: boolean;
  allowExtraStoreTrip: boolean;
  maxExtraStoreFee: number;
}

export interface ApiErrorEnvelope {
  message: string;
  error?: string;
  code?: string;
  traceId?: string;
  status?: number;
  details?: unknown;
}

export interface ContractPolicy {
  requiredHeaders: {
    traceId: string;
    clientRequestId: string;
    idempotencyKey: string;
  };
  errorShape: {
    required: Array<keyof ApiErrorEnvelope>;
    optional: Array<keyof ApiErrorEnvelope>;
  };
}

/**
 * Ninpo Snacks frontend contract policy for API callers.
 *
 * Required outbound request headers:
 * - x-trace-id: distributed trace correlation id.
 * - x-client-request-id: id unique per client request attempt.
 * - Idempotency-Key: required on backend-enforced mutation paths (order creation, approvals, and other high-risk writes).
 *
 * Expected backend error envelope shape:
 * {
 *   message: string;
 *   error?: string;
 *   code?: string;
 *   traceId?: string;
 *   status?: number;
 *   details?: unknown;
 * }
 */
export const API_CONTRACT_POLICY: ContractPolicy = {
  requiredHeaders: {
    traceId: 'x-trace-id',
    clientRequestId: 'x-client-request-id',
    idempotencyKey: 'Idempotency-Key'
  },
  errorShape: {
    required: ['message'],
    optional: ['error', 'code', 'traceId', 'status', 'details']
  }
};
